import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BackRow, Disclaimer, HelpButton, OptionButton, SkipLink } from "./shared";

const qTitle = (content, id) => content.questions.find((q) => q.id === id)?.title;

export function TeamSizeScreen({ content, teamSize, onChoose, onBack }) {
  const L = content.landing;
  return (
    <div className="p-7 md:p-10" data-testid="nm-team-screen">
      <BackRow onBack={onBack} progress="Before we start" />
      <div data-testid="nm-intro-blurb" className="mb-6 pb-5 border-b border-cvc-border">
        <p className="text-[11px] tracking-[0.22em] uppercase font-body font-semibold text-cvc-red">{L.eyebrow}</p>
        <p className="font-body text-sm text-cvc-ink mt-2 leading-relaxed">
          {L.body} <span data-testid="nm-intro-pill" className="font-semibold text-cvc-navy">{L.pill}</span>
        </p>
      </div>
      <h2 data-testid="nm-team-prompt" className="font-heading text-2xl md:text-3xl text-cvc-navy leading-snug">
        {content.team_size.prompt}
      </h2>
      <div className="mt-6 grid grid-cols-2 gap-2.5">
        {content.team_size.choices.map((c) => (
          <OptionButton key={c.id} testId={`nm-team-${c.id}`} selected={teamSize === c.id} onClick={() => onChoose(c.id)}>
            {c.label}
          </OptionButton>
        ))}
      </div>
    </div>
  );
}

function OtherInput({ value, onChange }) {
  return (
    <Textarea
      data-testid="nm-priority-other-text"
      placeholder="What would you most like to improve?"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-white"
      rows={3}
    />
  );
}

function ContinueRow({ onSkip, onNext, disabled }) {
  return (
    <div className="mt-7 flex items-center justify-between gap-3">
      <SkipLink testId="nm-priority-skip" onClick={onSkip} />
      <Button
        data-testid="nm-priority-next"
        onClick={onNext}
        disabled={disabled}
        className="bg-cvc-navy hover:bg-cvc-navy-deep text-cvc-cream rounded-full px-6 py-4 h-auto font-body disabled:opacity-50"
      >
        Continue
      </Button>
    </div>
  );
}

// §5 — 2–3 qualifying concerns.
export function PriorityScreen({ content, qualifying, priority, otherText, onSelect, setOtherText, onHelp, onSkip, onNext, onBack }) {
  return (
    <div className="p-7 md:p-10" data-testid="nm-priority-screen">
      <BackRow onBack={onBack} progress="Where to start" />
      <h2 className="font-heading text-2xl md:text-3xl text-cvc-navy leading-snug">{content.priority.prompt}</h2>
      <div className="mt-6 flex flex-col gap-2.5">
        {qualifying.map((a) => (
          <OptionButton key={a.questionId} testId={`nm-priority-${a.questionId}`}
            selected={priority === a.questionId} onClick={() => onSelect(a.questionId)}>
            {qTitle(content, a.questionId)}
          </OptionButton>
        ))}
        <OptionButton testId="nm-priority-other" selected={priority === "other"} onClick={() => onSelect("other")}>
          {content.priority.other_label}
        </OptionButton>
        {priority === "other" && <OtherInput value={otherText} onChange={setOtherText} />}
        <HelpButton testId="nm-priority-help" label={content.outcome_screen.help_label} onClick={onHelp} />
      </div>
      <ContinueRow onSkip={onSkip} onNext={onNext}
        disabled={!priority || priority === "skipped" || (priority === "other" && !otherText.trim())} />
    </div>
  );
}

// §6 — 4+ qualifying concerns: outcome-first.
export function OutcomeScreen({ content, outcomes, helpFirst, priority, otherText, onPickOutcome, onOther, setOtherText, onHelp, onSkip, onNext, onBack }) {
  const S = content.outcome_screen;
  const help = <HelpButton key="help" testId="nm-outcome-help" label={S.help_label} onClick={onHelp} />;
  return (
    <div className="p-7 md:p-10" data-testid="nm-outcome-screen">
      <BackRow onBack={onBack} progress="Where to start" />
      <h2 data-testid="nm-outcome-prompt" className="font-heading text-2xl md:text-3xl text-cvc-navy leading-snug">{S.prompt}</h2>
      <p className="font-body text-cvc-ink-soft mt-2 leading-relaxed">{S.subprompt}</p>
      <div className="mt-6 flex flex-col gap-2.5">
        {helpFirst && help}
        {outcomes.map((o) => (
          <OptionButton key={o.id} testId={`nm-outcome-${o.id}`} onClick={() => onPickOutcome(o)}>
            {S.outcomes[o.id]}
          </OptionButton>
        ))}
        {!helpFirst && help}
        <OptionButton testId="nm-outcome-other" selected={priority === "other"} onClick={onOther}>
          {S.other_label}
        </OptionButton>
        {priority === "other" && <OtherInput value={otherText} onChange={setOtherText} />}
      </div>
      {priority === "other" ? (
        <ContinueRow onSkip={onSkip} onNext={onNext} disabled={!otherText.trim()} />
      ) : (
        <div className="mt-7"><SkipLink testId="nm-priority-skip" onClick={onSkip} /></div>
      )}
    </div>
  );
}

// §7 — narrow a chosen outcome with 2–3 matching concerns.
export function NarrowScreen({ content, outcome, onSelect, onHelp, onBack }) {
  return (
    <div className="p-7 md:p-10" data-testid="nm-narrow-screen">
      <BackRow onBack={onBack} progress={content.outcome_screen.outcomes[outcome.id]} />
      <h2 className="font-heading text-2xl md:text-3xl text-cvc-navy leading-snug">{content.narrow_screen.prompt}</h2>
      <div className="mt-6 flex flex-col gap-2.5">
        {outcome.questions.slice(0, 3).map((id) => (
          <OptionButton key={id} testId={`nm-narrow-${id}`} onClick={() => onSelect(id)}
            sub={content.narrow_screen.descriptions[id]}>
            <span className="font-semibold">{qTitle(content, id)}</span>
          </OptionButton>
        ))}
        <HelpButton testId="nm-narrow-help" label={content.outcome_screen.help_label} onClick={onHelp} />
      </div>
    </div>
  );
}

// §9 — shown only when the owner asks for help choosing.
export function SuggestionScreen({ content, recommendedId, onAccept, onReject }) {
  const S = content.suggestion_screen;
  return (
    <div className="p-7 md:p-10" data-testid="nm-suggestion-screen">
      <BackRow onBack={onReject} progress="Help me choose" />
      <p className="text-[11px] tracking-[0.22em] uppercase font-body font-semibold text-cvc-red">{S.title}</p>
      <h2 data-testid="nm-suggestion-title" className="font-heading text-3xl md:text-4xl text-cvc-navy mt-3 leading-tight">
        {qTitle(content, recommendedId)}
      </h2>
      <p data-testid="nm-suggestion-explanation" className="font-body text-cvc-ink mt-4 leading-relaxed italic">
        &ldquo;{S.explanations[recommendedId]}&rdquo;
      </p>
      <p className="font-body text-sm text-cvc-ink-soft mt-4 leading-relaxed">{S.supporting}</p>
      <div className="mt-7 flex flex-wrap gap-3">
        <Button data-testid="nm-suggestion-accept" onClick={onAccept}
          className="bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-7 py-4 h-auto font-body">
          {S.accept_label}
        </Button>
        <Button data-testid="nm-suggestion-reject" variant="outline" onClick={onReject}
          className="border-cvc-navy text-cvc-navy hover:bg-cvc-navy hover:text-cvc-cream rounded-full px-6 py-4 h-auto font-body bg-transparent">
          {S.reject_label}
        </Button>
      </div>
      <Disclaimer text={content.disclaimer} testId="nm-suggestion-disclaimer" />
    </div>
  );
}
